import { Modal, Upload, Button, message } from "antd";
import { UploadOutlined, DownloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import { post } from "../../../../../common/services/httpClient";
import { API_ENDPOINTS } from "../../../../participants/api/apiEndpoints";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AgreementModalProps {
    open: boolean;
    onClose: () => void;
    companyId: number;
    companyName?: string;
    existingFile?: UploadFile[];
    onFileChange: (file: UploadFile[], rawFile?: RcFile) => void;
}
interface ApiResponse<T = unknown> {
    isSuccess?: boolean;
    isError?: boolean;
    message?: string;
    data?: T;
}

/*const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;*/


const AgreementModal = ({
    open,
    onClose,
    companyId,
    companyName,
    existingFile = [],
    onFileChange,
}: AgreementModalProps) => {

    const { t } = useTranslation();
    const [showButtons, setShowButtons] = useState(false);
    const [showSave, setShowSave] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string>("");


    // Fetch latest agreement file from server
    const fetchLatestAgreement = async () => {
        try {
            const response: any = await post("/CompanyException/GetByCompanyService", {
                companyId,
            });

            const agreementFilePath = response?.data?.agreementFilePath;

            if (agreementFilePath) {
                const fileObj: UploadFile = {
                    uid: "-1",
                    name: agreementFilePath.split("/").pop() || "agreement",
                    status: "done",
                    url: `${import.meta.env.VITE_API_BASE_URL.replace(/api$/, "")}${agreementFilePath}`,
                };

                onFileChange([fileObj]);
                setShowButtons(true); // show after fetch
                setShowSave(false);
                setSelectedFileName("");  // clear selected file name (because this is existing file)
            } else {
                onFileChange([]);
                setShowButtons(false);
                setSelectedFileName("");
                setShowSave(false);
            }

        } catch (error) {
            console.error(error);
            message.error(t("Agreement.loadFailed"));
            setShowButtons(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchLatestAgreement();
            setShowSave(false);
            setSelectedFileName("");
        }
    }, [open]);


    const handleBeforeUpload = (file: RcFile) => {

        const newFile: UploadFile = {
            uid: file.uid,
            name: file.name,
            status: "done",
            originFileObj: file,
            url: URL.createObjectURL(file),  //  important
        };

        setSelectedFileName(file.name);
        setShowSave(true);

        onFileChange([newFile], file);
        setShowButtons(false); // hide buttons until save
        return Upload.LIST_IGNORE;
    };

    // SAVE(UPLOAD FILE)
    const handleSave = async () => {
        const selectedFile = existingFile[0]?.originFileObj;

        if (!selectedFile) {
            message.error(t("Agreement.uploadRequired"));
            return;
        }

        const formData = new FormData();
        formData.append("companyId", companyId.toString());
        formData.append("agreementFile", selectedFile);

        try {
            const uploadResponse = await post<ApiResponse>(
                API_ENDPOINTS.EXCEPTIONS.UPLOAD_AGREEMENT,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (!uploadResponse?.isError) {
                message.success(t("Agreement.uploadSuccess"));
                await fetchLatestAgreement();  // fetch latest file again
                setShowButtons(true);
                setShowSave(false); // hide save after upload
            } else {
                message.error(uploadResponse?.message || t("Agreement.uploadFailed"));
            }
        } catch (error) {
            console.error(error);
            message.error(t("Agreement.uploadFailed"));
        }
    };

    const handleDeleteAgreement = async () => {
        Modal.confirm({
            title: t("Agreement.deleteConfirmTitle"),
            icon: <ExclamationCircleOutlined />,
            content: t("Agreement.deleteConfirmMessage"),
            okText: t("common.yesDelete"),
            okType: "danger",
            cancelText: t("common.cancel"),

            onOk: async () => {
                try {
                    const response = await post<ApiResponse>(
                        API_ENDPOINTS.EXCEPTIONS.DELETE,
                        { companyId }
                    );

                    if (!response?.isError) {
                        message.success(t("Agreement.deleteSuccess"));


                        // UI reset
                        onFileChange([]);
                        setShowButtons(false);
                        setShowSave(false);
                        setSelectedFileName("");
                    } else {
                        message.error(response?.message || t("Agreement.deleteFailed"));
                    }
                } catch (error) {
                    console.error(error);
                    message.error(t("Agreement.deleteFailed")); 
                }
            }
        });
    };

    return (
        <Modal
            title={
                <strong>
                    {/*{companyName ?? "Company"} - Agreement*/}
                    {companyName ?? t("Agreement.company")} - {t("Agreement.title")}
                </strong>
            }
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t("common.close")}
                </Button>,
                showSave && (
                    <Button key="save" type="primary" onClick={handleSave}>
                        {t("common.save")}
                    </Button>
                ),
            ]}
            destroyOnClose
        >
            <Upload
                fileList={existingFile}
                beforeUpload={handleBeforeUpload}
                onRemove={() => {
                    message.warning(t("Agreement.useDeleteButton"));
                    return false; // ❗ stop default remove
                }}
                showUploadList={false}
            >
                <Button icon={<UploadOutlined />}>
                    {existingFile.length ? t("Agreement.replace") : t("Agreement.upload")}
                </Button>
            </Upload>

            {showButtons && existingFile.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        href={existingFile[0].url}
                        target="_blank"
                    >
                        {t("Agreement.view")}
                    </Button>

                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleDeleteAgreement}
                    />


                </div>
            )}

            {selectedFileName && (
                <div style={{ marginTop: 10, fontWeight: "bold" }}>
                    {t("Agreement.selectedFile")}: {selectedFileName}
                    {/*Selected File: {selectedFileName}*/}
                </div>
            )}



        </Modal>
    );
};

export default AgreementModal;
